import { MenuItem } from '@mui/material';
import classnames from 'classnames';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { MouseEvent, ReactNode, VoidFunctionComponent } from 'react';

import {
  getSortIconName,
  isAnySortSelected,
  isSortedByDirection,
  isSortSelected,
} from '../../../../../shared/projectsV2/helpers';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Icon, { type IconClassName, icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import { StickyMenu } from '../../../generic/Menu';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectCategory } from '../../../../../shared/projectsV2/common';
import type { ProjectSortPrefix, ProjectsSort } from '../../../../../shared/projectsV2/sorting';

export type SortCategory = {
  key: string;
  label: string | ((context: SortingContext) => string);
  icon?: IconClassName;
  prefix: ProjectSortPrefix;
  isVisible?: (context: SortingContext) => boolean;
  wrapper?: (children: ReactNode) => ReactNode;
};

export type SortingContext = {
  category: ProjectCategory;
  sort: ProjectsSort;
};

export type SortingDropdownConfig = {
  categories: SortCategory[];
  defaultCategoryKey: string;
  menuId: string;
  getConfig: (context: SortingContext) => {
    categories: SortCategory[];
  };
};

type SortingDropdownProps = {
  config: SortingDropdownConfig;
};

const SortingDropdown: VoidFunctionComponent<SortingDropdownProps> = ({ config }) => {
  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const setSort = useFetchProjectsAction(ProjectsV2Store.actions.setSort);

  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>(config.defaultCategoryKey);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // Create context for config functions
  const context: SortingContext = { category, sort };

  // Get processed config with current context
  const processedConfig = config.getConfig(context);
  const visibleCategories = processedConfig.categories.filter(cat => !cat.isVisible || cat.isVisible(context));

  // Get current selected category
  const selectedCategory = visibleCategories.find(cat => cat.key === selectedCategoryKey) || visibleCategories[0];

  // Get all prefixes for icon logic
  const allPrefixes = config.categories.map(cat => cat.prefix);

  // Check if any category is active
  const isAnyActive = isAnySortSelected(sort, allPrefixes);

  // Get header icon
  const getHeaderIcon = (): IconClassName => {
    const iconName = getSortIconName(sort, allPrefixes);
    return icons[iconName as keyof typeof icons];
  };

  // Get label (handle both string and function)
  const getLabel = (cat: SortCategory): string => {
    return _.isFunction(cat.label) ? cat.label(context) : cat.label;
  };

  // Handlers
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSetCategory = (categoryKey: string) => {
    setSelectedCategoryKey(categoryKey);
    setSort(`${categoryKey}_desc` as ProjectsSort);
    handleClose();
  };

  const handleSetDirection = (direction: 'asc' | 'desc') => {
    // Use current category from sort state
    const currentCategoryKey = sort.split('_').shift();
    const validKeys = config.categories.map(cat => cat.key);
    const categoryKey = validKeys.includes(currentCategoryKey!)
      ? currentCategoryKey
      : config.defaultCategoryKey;
    setSort(`${categoryKey}_${direction}` as ProjectsSort);
    handleClose();
  };

  // Update selected category based on current sort
  useEffect(() => {
    const currentKey = sort.split('_').shift();
    const validKeys = config.categories.map(cat => cat.key);
    if (validKeys.includes(currentKey!)) {
      setSelectedCategoryKey(currentKey!);
    } else {
      setSelectedCategoryKey(config.defaultCategoryKey);
    }
  }, [sort, config.categories, config.defaultCategoryKey]);

  if (!selectedCategory) {
    return null; // Safety check
  }

  return (
    <div>
      <Button
        type="text"
        isFullWidth
        onClick={handleClick}
      >
        <div
          className={classnames(
            'projects-table__header__column',
            {
              'projects-table__header_active': isAnyActive,
            },
          )}
        >
          <Flex gap="8px" justify="flex-start">
            <span className="projects-table__header__label">
              {selectedCategory.icon && <Icon icon={selectedCategory.icon} />}
              {getLabel(selectedCategory)}
            </span>
            <span className="projects-table__header__sort-icon">
              <Icon
                icon={getHeaderIcon()}
                size={ICON_SIZE.LG}
              />
            </span>
          </Flex>
        </div>
      </Button>

      <StickyMenu
        id={config.menuId}
        anchorEl={anchorEl}
        open={isOpen}
        popperClassName="projects-dashboard-v2 projects-table projects-table__header"
        paperClassName="projects-table__header__menu"
        onClose={handleClose}
      >
        {/* Categories section - only show if multiple categories */}
        {visibleCategories.length > 1 && (
          <div className="projects-table__header__categories">
            {visibleCategories.map((cat) => {
              const menuItem = (
                <MenuItem
                  key={cat.key}
                  className="projects-table__header__item"
                  onClick={() => handleSetCategory(cat.key)}
                  selected={isSortSelected(sort, cat.prefix)}
                >
                  <Flex justify="space-between" grow={1}>
                    <span className="projects-table__header__label">
                      {cat.icon && <Icon icon={cat.icon} />}
                      {getLabel(cat)}
                    </span>
                    {isSortSelected(sort, cat.prefix) && (
                      <Icon icon={icons.check} />
                    )}
                  </Flex>
                </MenuItem>
              );

              return cat.wrapper ? cat.wrapper(menuItem) : menuItem;
            })}

            {/* Separator */}
            <div className="projects-table__header__separator" />
          </div>
        )}

        {/* Direction Options */}
        <MenuItem
          className="projects-table__header__direction"
          onClick={() => handleSetDirection('asc')}
          selected={isAnyActive && isSortedByDirection(sort, 'asc')}
        >
          <Flex justify="space-between" grow={1}>
            <span className="projects-table__header__label">
              <Icon icon={icons.arrowUp} />
              Ascending
            </span>
            {isAnyActive && isSortedByDirection(sort, 'asc') && (
              <Icon icon={icons.check} />
            )}
          </Flex>
        </MenuItem>
        <MenuItem
          className="projects-table__header__direction"
          onClick={() => handleSetDirection('desc')}
          selected={isAnyActive && isSortedByDirection(sort, 'desc')}
        >
          <Flex justify="space-between" grow={1}>
            <span className="projects-table__header__label">
              <Icon icon={icons.arrowDown} />
              Descending
            </span>
            {isAnyActive && isSortedByDirection(sort, 'desc') && (
              <Icon icon={icons.check} />
            )}
          </Flex>
        </MenuItem>
      </StickyMenu>
    </div>
  );
};

export default SortingDropdown;
